// Circular Startup Index with Search

import React, { useEffect, useState } from 'react';
import aa from 'search-insights';
import csiModalBg from '../../../../images/csiModalBg.png';
import csiTileBg from '../../../../images/csiTileBg.png';
import image from '../../../../images/csiLogo.png';
import Modal from '@components/modal/modal';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import {
  ClearRefinements,
  CurrentRefinements,
  Hits,
  InstantSearch,
  Pagination,
  RefinementList,
  SearchBox,
} from 'react-instantsearch';
import CustomStats from '../../components/search/search-stats';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { navigate } from 'gatsby';
import { SearchIconAtom } from '@atoms/icon-atoms/nav-icon-atoms';

interface WindowSizeProps {
  width?: number;
  height?: number;
}

interface CSISearchProps {
  className?: string;
}

interface ModalContentProps {
  Active_Work_Areas?: string[];
  Audience?: string;
  Circular_Economy_Activity?: string;
  Headquarters?: string;
  Last_Modifeid?: string;
  Latest_Funding?: string;
  Map_Marker_Address?: string;
  Operating_Regions?: string[];
  Stage__Startup_or_Scale_up_?: string;
  Startup_Description?: string;
  Startup_Name?: string;
  Website?: string;
  Year_Founded?: string;
  objectID?: string;
  __position?: number;
}

aa('init', {
  appId: process.env.GATSBY_ALGOLIA_APP_ID,
  apiKey: process.env.GATSBY_CSI_ALGOLIA_API_KEY,
  useCookie: true,
});

const algoliasearch = require('algoliasearch');
const CSI_INDEX = process.env.GATSBY_CSI_INDEX;
const client = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_CSI_ALGOLIA_API_KEY,
  CSI_INDEX
);

const index = client.initIndex(CSI_INDEX);

const CSISearch: React.FC<CSISearchProps> = ({ className }) => {
  const attrs = [
    'Operating_Regions',
    'searchable(Headquarters)',
    'Active_Work_Areas',
    'Audience',
    'Year_Founded',
  ];

  const attrsNames = [
    'Operating geographies',
    'HQ country',
    'Industry/sector',
    'Customer type',
    'Year Founded',
  ];

  function useWindowSize() {
    const [windowSize, setWindowSize] = useState<WindowSizeProps>({
      width: typeof window !== 'undefined' ? window.innerWidth : undefined,
      height: typeof window !== 'undefined' ? window.innerHeight : undefined,
    });
    useEffect(() => {
      function handleResize() {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowSize;
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ModalContentProps>({});
  const [modalContentChecked, setModalContentChecked] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [localData, setLocalData] = useState([]);
  const size = useWindowSize();
  const breakPointTablet = 768;

  function toggleResultsFilters() {
    setShowFilters(!showFilters);
  }

  useEffect(() => {
    const handleHashChange = () => {
      if (!window.location.hash) {
        setModalOpen(false);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    let allAlgoliaObjects = [];
    const getLocal = () => {
      index
        .browseObjects({
          batch: batch => {
            allAlgoliaObjects = allAlgoliaObjects.concat(batch);
          },
        })
        .then(() => {
          setLocalData(allAlgoliaObjects);
        });
    };
    getLocal();
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (localData.length <= 0) return;

    if (hash && hash !== startupToURLHash(modalContent)) {
      let hit = null;

      for (let index = 0; index < localData.length; index++) {
        const startup = localData[index];

        if (startupToURLHash(startup) === hash) {
          hit = startup;
          setModalContentChecked(true);
          setModalOpen(true);
          setModalContent(startup);
          break;
        }
      }

      if (!hit) {
        setModalContent({});
        navigate('/resources/business/circular-startup-index');
        console.warn(
          'bad url hash - does not correspond to an Algolia result',
          hash
        );
      }
    }
  }, [modalOpen, modalContentChecked, modalContent, localData]);

  function startupToURLHash(startup) {
    let result = null;
    if (startup.Startup_Name) {
      result = startup.Startup_Name.replace(/\s/g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''); // replace special characters
    }
    return '#' + result;
  }

  function Hit({ hit, func: funcHandler }) {
    return (
      <ResultTile
        onClick={() => funcHandler(hit)}
        data-gtm-id={GTM_ID.CSI_TILE}
      >
        <p className="startup-name">{hit.Startup_Name}</p>
        <p className="from-country">
          FROM{' '}
          <span className="tile-country">{hit.Headquarters.toUpperCase()}</span>
        </p>
        <p className="startup-text">{hit.Startup_Description}</p>
        <div className="tile-work-areas">
          {hit.Active_Work_Areas &&
            hit.Active_Work_Areas.map((el, index) => {
              return (
                <div className="tile-tags" key={index}>
                  {el}{' '}
                </div>
              );
            })}
        </div>
      </ResultTile>
    );
  }

  function openStartupCard(hit) {
    let startupNameURL = startupToURLHash(hit);
    setModalContent(hit);
    setModalOpen(true);
    navigate(`/resources/business/circular-startup-index${startupNameURL}`, {
      replace: false,
    });
  }

  function closeStartupCard() {
    window.history.pushState('', document.title, window.location.pathname);
    setModalOpen(false);
  }

  function cleanWebsiteUrl(website) {
    // if it does not start with http or https, prepend with https://
    if (website) {
      if (website.slice(0, 4) !== 'http') {
        website = 'https://' + website;
      }
    }

    return website;
  }

  return (
    <ContentWrapper>
      <StyledModal open={modalOpen} onClose={closeStartupCard}>
        <div className="modal-row-1">
          <div className="modal-startup-title">{modalContent.Startup_Name}</div>
          <div className="row-3-block-title">
            {modalContent.Headquarters !== undefined && (
              <span className="country">
                <span>FROM </span>
                {modalContent.Headquarters.toUpperCase()}
              </span>
            )}
          </div>
        </div>

        <div className="modal-row-3">
          <div className="row-3-block">
            {modalContent?.Startup_Description &&
              modalContent.Startup_Description?.length > 0 && (
                <div className="modal-paragraph">
                  <span className="startup-prompt">
                    How would you introduce your startup?
                  </span>
                  "{modalContent.Startup_Description}"
                </div>
              )}
          </div>
          <div className="row-3-block">
            <div className="row-3-block-title">
              {modalContent?.Circular_Economy_Activity &&
                modalContent.Circular_Economy_Activity?.length > 0 && (
                  <span className="startup-prompt">
                    What makes your organisation circular?
                  </span>
                )}
            </div>
            {modalContent?.Circular_Economy_Activity &&
              modalContent.Circular_Economy_Activity?.length > 0 && (
                <div className="modal-paragraph">
                  {' '}
                  "{modalContent.Circular_Economy_Activity}"{' '}
                </div>
              )}
          </div>
        </div>
        <div className="modal-row-4">
          <span className="website-text">
            <a
              href={cleanWebsiteUrl(modalContent.Website)}
              target="_blank"
              rel="noopener noreferrer"
              data-gtm-id={GTM_ID.CSI_TILE_LINK}
            >
              {' '}
              {modalContent.Website}{' '}
            </a>
          </span>
          <span>Founded in {modalContent.Year_Founded}</span>
        </div>
        <div className="modal-row-5">
          <div className="modal-row-5-block">
            <div className="modal-row-5-block-title">Operating Regions</div>
            <div className="modal-row-5-tag-container">
              {modalContent.Operating_Regions &&
                modalContent.Operating_Regions.map((el, index) => {
                  return (
                    <div className="modal-row-5-tags" key={index}>
                      {el}
                    </div>
                  );
                })}
            </div>
          </div>
          <div className="modal-row-5-block">
            <div className="modal-row-5-block-title">Active Work Areas</div>
            <div className="modal-row-5-tag-container">
              {modalContent.Active_Work_Areas &&
                modalContent.Active_Work_Areas.map((el, index) => {
                  return (
                    <div className="modal-row-5-tags" key={index}>
                      {el}{' '}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
        <div className="modal-row-6">
          <p>
            Inclusion in this Index is not an endorsement. Information has been
            provided by the startup and has not been verified by the Ellen
            MacArthur Foundation. For more information, visit the{' '}
            <a
              href="https://www.ellenmacarthurfoundation.org/resources/business/circular-startup-index#csi-disclaimer"
              data-gtm-id={GTM_ID.CSI_TILE_DISCLAIMER}
            >
              main Index page
            </a>
          </p>
        </div>
      </StyledModal>
      <InstantSearch searchClient={client} indexName={CSI_INDEX} routing={true}>
        <HeaderRow>
          <div className="header-row-image">
            <img src={image} alt="csi-logo" />
          </div>
          <SearchWrapper>
            <SearchBox
              searchAsYouType={true}
              className="searchbox"
              placeholder="Search for a country, industry or company name..."
            />
            <SearchIconAtom width="26" height="26" fill={vars.coreDarkGrey} />
          </SearchWrapper>
        </HeaderRow>
        <ResultsAndFilters>
          <Filters
            style={
              size.width !== undefined &&
              (size.width > breakPointTablet || showFilters)
                ? {}
                : { display: 'none' }
            }
          >
            {attrs.map((filter, index) => {
              return (
                <div className="filter-block" key={index}>
                  <div className="accordion">
                    <div className="accordion-item">
                      <div className="accordion-title">
                        <div className="filter-heading">
                          {filter !== attrs[1] && (
                            <span>{attrsNames[index]}</span>
                          )}
                          {filter === attrs[1] && <span>HQ country</span>}
                        </div>
                      </div>
                      <div className="accordion-content">
                        {filter !== attrs[4] &&
                          filter !== attrs[1] &&
                          filter !== attrs[2] && (
                            <div>
                              <RefinementList
                                attribute={filter}
                                limit={10}
                                showMore={true}
                                showMoreLimit={25}
                              />
                            </div>
                          )}
                        {filter === attrs[1] && (
                          <div>
                            <RefinementList
                              attribute="Headquarters"
                              limit={10}
                              showMore={true}
                              showMoreLimit={100}
                              searchable={true}
                            />
                          </div>
                        )}
                        {filter === attrs[2] && (
                          <div>
                            <RefinementList
                              attribute={filter}
                              limit={10}
                              sortBy={['name:asc']}
                              showMore={true}
                              showMoreLimit={100}
                            />
                          </div>
                        )}
                        {filter === attrs[4] && (
                          <div>
                            <RefinementList
                              attribute={filter}
                              sortBy={['name:desc']}
                              limit={10}
                              showMore={true}
                              showMoreLimit={25}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {size.width !== undefined && size.width <= breakPointTablet && (
              <CloseFilters onClick={() => setShowFilters(false)}>
                Apply Filters
              </CloseFilters>
            )}
          </Filters>
          <Results>
            <div className="custom-refinements">
              <CurrentRefinements />
              <ClearRefinements
                translations={{
                  resetButtonText: 'clear filters',
                }}
              />
            </div>
            <StyledCustomStats />
            <Hits
              hitComponent={({ hit }) => Hit({ hit, func: openStartupCard })}
              data-gtm-id={GTM_ID.CSI_HITS}
            />
            <Pagination padding={1} showFirst={true} showLast={true} />
          </Results>
          {size.width !== undefined && size.width <= breakPointTablet && (
            <button
              onClick={toggleResultsFilters}
              className="toggle"
              data-gtm-id={GTM_ID.CSI_MOBILE_TOGGLE}
            >
              {showFilters ? 'Results' : 'Filters'}
            </button>
          )}
        </ResultsAndFilters>
      </InstantSearch>
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  max-width: ${vars.contentMaxWidth};
  margin: 0 auto;
  width: 100%;
  padding: 0;

  @media only screen and (min-width: ${vars.breakPointTablet}) {
    padding-left: ${vars.spacingS};
    padding-right: ${vars.spacingS};
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;

  svg {
    position: absolute;
    top: 13px;
    left: 10px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  margin: ${vars.spacingXl} auto 0 auto;
  padding: 0;
  width: ${vars.contentMaxWidth};
  max-width: 100%;
  justify-content: space-between;

  .header-row-image {
    min-width: 20%;
    margin-bottom: ${vars.spacingL};
  }

  .searchbox {
    min-width: 70%;
    width: 100%;
  }

  .ais-SearchBox-submit {
    display: none;
  }

  .ais-SearchBox-reset {
    display: none;
  }

  input {
    font-weight: ${vars.fontWeightRegular};
    width: calc(100% - 1.5rem);
    border: 0;
    background-color: #f3f3f9;
    padding: ${vars.spacingM};
    padding-left: ${vars.spacingXxl};
    font-size: 1.6rem;
  }

  @media only screen and (max-width: ${vars.breakPointTablet}) {
    display: flex;
    flex-wrap: wrap;

    margin-bottom: ${vars.spacingXl};

    .searchbox {
      min-width: 100%;
      padding-left: 0rem;
    }

    img {
      max-width: 100%;
      height: auto;
    }
  }
`;

const StyledCustomStats = styled(CustomStats)`
  padding-top: 0px;
  padding-bottom: 1rem;
`;

const ResultsAndFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin: 0 auto ${vars.spacingXl};
  padding: 0;
  width: ${vars.contentMaxWidth};
  width: 100%;
  font-weight: ${vars.fontWeightRegular};

  @media only screen and (max-width: ${vars.breakPointTablet}) {
    flex-direction: column;
    align-items: center;
  }

  .ais-ClearRefinements {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 6rem;

    .ais-ClearRefinements-button--disabled {
      display: none;
    }

    button {
      font-weight: ${vars.fontWeightRegular};
      font-size: ${vars.fontSize1};
      background-color: white;
      color: black;
      border: 0px;
    }
  }

  .custom-refinements {
    display: flex;
    justify-content: flex-start;
    width: 100%;
    flex-wrap: wrap;
    font-size: ${vars.fontSize1};
    margin-bottom: ${vars.spacingS};

    .ais-CurrentRefinements-list {
      li {
        margin-bottom: ${vars.spacingS};
      }

      .ais-CurrentRefinements-item {
        padding: 0;
        display: flex;
        flex-wrap: wrap;

        .ais-CurrentRefinements-label {
          display: none;
        }

        .ais-CurrentRefinements-category {
          margin-left: 1rem;
          padding-right: 1rem;
          display: flex;
          font-weight: ${vars.fontWeightRegular};
          justify-content: space-around;
          background-color: ${vars.cardBorderGrey};
          border-radius: 3px;
          height: 2.8rem;
          margin-bottom: ${vars.spacingS};

          span {
            font-weight: ${vars.fontWeightRegular};
            margin-right: 1rem;
            margin-left: 1rem;
          }

          .ais-CurrentRefinements-categoryLabel {
            display: flex;
            align-items: center;
            white-space: nowrap;
          }

          button {
            border: none;
            font-weight: ${vars.fontWeightExtraBold};
            background-color: ${vars.cardBorderGrey};
          }
        }
      }
    }
  }

  .ais-RefinementList-labelText {
    span {
      font-weight: ${vars.fontWeightRegular};
    }
  }

  .ais-Refinement-List {
    ais-RefinementList-noResults {
      font-weight: ${vars.fontWeightRegular};
      font-size: 1.5rem;
    }
  }

  .toggle {
    font-weight: ${vars.fontWeightMedium};
    font-size: 18px;
    padding: 12px 36px;
    background-color: ${vars.coreDarkGrey};
    border-radius: 24px;
    margin-top: ${vars.spacingM};
    cursor: pointer;
    color: white;
    border: 0;
    position: sticky;
    bottom: 40px;
  }
`;

const CloseFilters = styled.div`
  font-weight: ${vars.fontWeightMedium};
  font-size: 1.4rem;
  position: fixed;
  bottom: 120px;
  right: 24px;
  background-color: blue;
  color: white;
  border-radius: 24px;
  padding: 12px 24px;
  cursor: pointer;
`;

const Filters = styled.div`
  width: 100%;
  min-width: 100px;
  padding-right: 1.5rem;
  flex-direction: column;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${vars.white};
  padding: ${vars.spacingL};
  z-index: 999;
  overflow: auto;

  @media only screen and (min-width: ${vars.breakPointTablet}) {
    position: relative;
    padding: 0;
    padding-top: 3.2rem;
    padding-right: ${vars.spacingS};
    width: 20%;
    z-index: 0;
  }

  @media only screen and (max-width: ${vars.breakPointSmallTablet}) {
    span {
      ${vars.fontSize7}
    }
  }

  @media only screen and (max-width: ${vars.breakPointTablet}) {
    width: 100%;

    span {
      ${vars.fontSize7}
    }
  }

  .ais-SearchBox-input {
    font-weight: ${vars.fontWeightRegular};
    font-size: 1.5rem;
    padding: ${vars.spacingS};
    margin-bottom: ${vars.spacingS};
    border-radius: 4px;
    border: 1px solid lightGrey;
    width: 100%;
    @media only screen and (min-width: ${vars.breakPointTablet}) {
      width: calc(100% - 1.5rem);
    }
  }

  .ais-RefinementList-noResults {
    font-weight: ${vars.fontWeightRegular};
    font-size: 1.5rem;
  }

  .ais-SearchBox-submit,
  .ais-SearchBox-reset {
    display: none;
  }

  .filter-block {
    margin-bottom: ${vars.spacingL};
  }

  .filter-heading {
    margin-bottom: ${vars.spacingS};
    display: flex;
    justify-content: space-between;

    span {
      font-weight: ${vars.fontWeightBold};
      color: #f26651;
      font-size: ${vars.fontSize3};
    }
  }

  .ais-RefinementList {
    .ais-RefinementList-showMore--disabled {
      display: none;
    }

    .ais-RefinementList-showMore {
      font-weight: ${vars.fontWeightMedium};
      background: none;
      border: 0;
      margin-top: ${vars.spacingXs};
      opacity: 0.6;
      transition: 0.2s ease-in-out;

      &:hover {
        opacity: 1;
        cursor: pointer;
      }
    }
  }

  .ais-RefinementList-item {
    list-style: none;

    .ais-RefinementList-labelText {
      font-weight: ${vars.fontWeightRegular};
      font-size: ${vars.fontSize3} span.ais-Highlight {
        mark.ais-Highlight-highlighted {
          font-weight: ${vars.fontWeightRegular};
        }
      }
    }
  }

  .ais-RefinementList-label {
    display: flex;
    align-items: center;
    margin-bottom: ${vars.spacingXs};
  }

  .ais-RefinementList-count {
    display: none;
  }

  .ais-RefinementList-checkbox {
    margin-right: ${vars.spacingXs};
  }

  @media only screen and (max-width: 600px) {
    // stack filters on top of tiles when at mobile size
    width: 100%;
    min-width: 110px;

    display: flex;

    .filter-block {
      min-width: calc(100% / 2.9);
    }
  }
`;

const Results = styled.div`
  width: 80%;

  @media only screen and (max-width: ${vars.breakPointTablet}) {
    width: 100%;
  }

  .clear-filters-button {
    height: 3.7rem;
    width: 11rem;
  }

  .ais-ClearRefinements-button {
    cursor: pointer;
    height: 3rem;
    width: 6rem;
    margin-left: 2rem;
  }

  .ais-CurrentRefinements-delete {
    cursor: pointer;
  }

  .cancel-filter-button {
    border: 2px solid black;
    height: 2rem;
    width: 2rem;
  }

  .startup-name {
    font-size: ${vars.fontSize7};
    padding-bottom: ${vars.spacingXs};
    font-weight: ${vars.fontWeightSemiBold};
  }

  .from-country {
    color: #f26651;
    font-weight: ${vars.fontWeightRegular};
    font-size: ${vars.fontSize4};
    height: 4rem;

    .tile-country {
      color: #f26651;
      font-weight: ${vars.fontWeightRegular};
      font-weight: ${vars.fontWeightBold};
    }
  }

  .startup-text {
    text-overflow: ellipsis;
    font-size: ${vars.fontSize2};
    font-weight: ${vars.fontWeightRegular};
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .selected-filters {
    display: flex;
    margin-bottom: ${vars.spacingM};
    font-size: ${vars.fontSize2};
  }

  .ais-Hits-list {
    list-style: none;
    padding: 0;
    justify-content: flex-start;
  }

  @media only screen and (max-width: 1026px) {
    .ais-Hits-list {
      li {
        width: calc((100% / 2) - 1.5rem);
      }
    }
  }

  @media only screen and (max-width: ${vars.breakPointSmallTablet}) {
    .ais-Hits-list li {
      min-width: 70%;
      width: 100%;
      margin-right: 0;
    }
  }

  .ais-Pagination {
    display: flex;
    justify-content: center;
  }

  .ais-Pagination-list {
    display: flex;

    .ais-Pagination-item--selected {
      a {
        color: ${vars.coreDarkGrey};
      }
    }

    .ais-Pagination-link {
      font-weight: ${vars.fontWeightMedium};
      font-size: ${vars.fontSize2};
      width: 3.5rem;
      height: 3.5rem;
      color: #d0d0e3;
      text-decoration: none;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .ais-Pagination-item {
      display: flex;
      list-style: none;
      border: 2px solid gray;
      justify-content: center;
      margin-left: 1rem;
      border-radius: 0.4rem;
      box-shadow: 1px 2px 2px #d0d0e3;
      border: 1px #d0d0e3 solid;
    }
  }

  .ais-Hits-list {
    display: flex;
    flex-wrap: wrap;
  }

  .ais-Hits-item {
    background-image: url(${csiTileBg});
    background-position: top;
    background-size: cover;
    width: calc((100% / 3) - 1.5rem);
    margin-right: 1.5rem;
    margin-bottom: 1.5rem;
    border: 1px solid ${vars.cardBorderGrey};
    border-radius: 5px;
    box-shadow: 1px 2px 2px #d0d0e3;

    &:hover {
      cursor: pointer;
    }
  }
`;

const ResultTile = styled.div`
  margin-bottom: ${vars.spacingL};
  width: 100%;
  padding: 2.5rem;

  @media only screen and (max-width: ${vars.breakPointTablet}) {
    p {
      ${vars.fontSize6}
    }
    .startup-name {
      font-size: 2.4rem;
    }
  }

  .tile-work-areas {
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: start;
    margin-top: ${vars.spacingS};
  }

  .tile-tags {
    ${vars.fontSize1};
    display: flex;
    max-width: 15rem;
    justify-content: center;
    align-items: center;
    height: 2.4rem;
    background-color: #d0d0e3;
    border-radius: 6px;
    font-weight: ${vars.fontWeightMedium};
    margin-right: 0.5rem;
    margin-top: 0.5rem;
    padding-left: 0.8rem;
    padding-right: 0.8rem;
  }
`;

const StyledModal = styled(Modal)`
  .modal-overlay {
    background-color: rgba(0, 0, 0, 0.85);
  }

  .modal-container {
    background-image: url(${csiModalBg});
    background-position: top left;
    background-repeat: no-repeat;
    padding: 3rem;
    border-radius: 8px;
    overflow: auto;
    height: 80%;
    width: 70%;
    display: flex;
    flex-direction: column;

    @media only screen and (min-width: ${vars.breakPointTablet}) {
      padding: 5rem;
    }

    .modal-close {
      height: 1.5rem;
      width: 1.5rem;
      svg {
        height: 100%;
        width: 100%;
      }
    }

    .modal-row-6 {
      margin-top: auto;

      p {
        font-weight: ${vars.fontWeightRegular};
      }

      @media only screen and (max-width: ${vars.breakPointTablet}) {
        width: 100%;
        font-size: 1.3rem;
      }
    }

    @media only screen and (max-width: ${vars.breakPointSmallTablet}) {
      div {
        flex-direction: column;
        width: 100%;
      }

      .modal-row-5-tag-container {
        flex-direction: row;

        .modal-row-5-tags {
          width: 17rem;
        }
      }
    }

    @media only screen and (max-width: 1100px) {
      height: 90%;
      width: 90%;
    }

    @media only screen and (max-width: ${vars.breakPointTablet}) {
      ${vars.fontSize6};

      .modal-paragraph {
        margin-bottom: 3rem;
      }

      .modal-row-5-block {
        margin-bottom: 2rem;
      }

      .modal-row-5-tags {
        ${vars.fontSize3};
        width: 17rem;
      }

      .modal-close {
        height: 1.9rem;
        width: 1.9rem;
      }
    }

    @media only screen and (max-width: ${vars.breakPointTenColumn}) {
      ${vars.fontSize5};

      .modal-row-5-tags {
        ${vars.fontSize2};
      }
    }

    .modal-close {
      cursor: pointer;
    }

    div {
      display: flex;
      font-weight: ${vars.fontWeightRegular};
    }
  }

  .modal-row-1 {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    margin-bottom: 3rem;
    font-weight: ${vars.fontWeightRegular};
    font-size: 3rem;
    vertical-align: baseline;

    img {
      display: none;
    }
  }

  @media only screen and (max-width: ${vars.breakPointTablet}) {
    img {
      max-height: 6.5rem;
    }
  }

  .modal-row-2 {
    font-weight: ${vars.fontWeightRegular};
    font-size: ${vars.fontSize9};
  }

  .modal-row-3 {
    justify-content: space-between;
  }

  .row-3-block {
    display: flex;
    flex-direction: column;
    width: 48%;
  }

  .modal-paragraph {
    font-size: ${vars.fontSize4};
    flex-direction: column;
  }

  .row-3-block-title {
    font-weight: ${vars.fontWeightRegular};
    font-size: ${vars.fontSize6};
    color: #f26651;

    span {
      color: #f26651;
      font-weight: ${vars.fontWeightBold};
    }
  }
  span.startup-prompt {
    font-size: ${vars.fontSize3};
    color: #f26651;
    font-weight: ${vars.fontWeightBold};
  }

  .modal-row-4 {
    flex-direction: column;
    justify-content: end;
    height: 5rem;
    font-size: ${vars.fontSize4};
    margin-bottom: 2rem;

    .website-text {
      text-decoration: underline;
      height: 2rem;
    }

    @media only screen and (max-width: ${vars.breakPointTenColumn}) {
      justify-content: space-between; // ad break for mobile
      height: 7rem;
    }
  }

  .modal-row-5 {
    justify-content: space-between;
  }

  .modal-row-5-block {
    width: 48%;
    display: flex;
    flex-direction: column;
    justify-content: normal;
    font-size: ${vars.fontSize3};

    .modal-row-5-block-title {
      font-weight: ${vars.fontWeightBold};
      color: #f26651;
    }

    .modal-row-5-tag-container {
      justify-content: flex-start;
      flex-wrap: wrap;
    }
  }

  .modal-row-5-tags {
    height: 2.5rem;
    padding-left: ${vars.spacingXs};
    padding-right: ${vars.spacingXs};
    background-color: #d0d0e3;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    font-size: ${vars.fontSize1};
    margin-right: 1rem;
    margin-bottom: 0.5rem;
    margin-top: 0.5rem;
    width: 17rem;
  }

  .country {
    color: #f26651;
    ${vars.fontSize6};
    font-weight: ${vars.fontWeightBold};

    span:first-of-type {
      font-weight: ${vars.fontWeightRegular};
    }
  }
`;

export default CSISearch;
